// React & React Router
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";

// Form Handling
import { useForm } from "react-hook-form";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { updateBannerFunc } from "../../../../store/custom-actions/banners/UpdateBannerSlicer";
import { getSingleBannerFunc } from "../../../../store/custom-actions/banners/GetSingleBannerSlicer";

// i18n
import { useTranslation } from "react-i18next";

// UI Components
import PageTitle from "../../../layouts/PageTitle";
import FormField from "../../../components/ui/FormField";
import TextAreaField from "../../../components/ui/TextAreaField";
import CheckboxField from "../../../components/ui/CheckboxField";
import SubmitButton from "../../../components/ui/SubmitButton";
import MediaSelectorButton from "../../../components/media/MediaSelectorButton";
import LanguageSwitcherTabs from "../../../components/ui/LanguageSwitcherTabs";

// Utilities
import { getLocalizedTranslation } from "../../../../utils/localization";

function UpdateBannerForm() {
  const { t } = useTranslation();
  const { bannerId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeLang, setActiveLang] = useState();
  const [imageDefault, setImageDefault] = useState([]);
  const [imageMobDefault, setImageMobDefault] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  const { loading: submitLoading } = useSelector(
    (state) => state.updateBannerSlicer
  );
  const { loading: languageLoading, data: languageData } = useSelector(
    (state) => state.getLanguageSettingsSlicer
  );
  const { data: bannerData, loading } = useSelector(
    (state) => state.getSingleBannerSlicer
  );

  // Fetch single banner
  useEffect(() => {
    if (bannerId) dispatch(getSingleBannerFunc(bannerId));
  }, [dispatch, bannerId]);

  // Handle language switching
  useEffect(() => {
    if (languageData?.defaultLang?.slug) {
      setActiveLang(languageData.defaultLang.slug);
    }
  }, [languageData]);

  // Populate form data after banner fetch
  useEffect(() => {
    if (!bannerData) return;
    const translations = getLocalizedTranslation(
      bannerData.translations,
      languageData?.defaultLang?.slug
    );

    setImageDefault(bannerData.img ? [bannerData.img] : []);
    setImageMobDefault(bannerData.mob_img ? [bannerData.mob_img] : []);
    reset(buildFormDefaults(bannerData, translations));
  }, [bannerData, languageData, reset]);

  // Update form when activeLang changes
  useEffect(() => {
    if (!bannerData?.translations || !activeLang) return;

    const translation = bannerData.translations.find(
      (tr) => tr.locale === activeLang
    );
    setValue("title", translation?.title || "");
    setValue("description", translation?.description || "");
    setValue("btn_text", translation?.btn_text || "");
  }, [activeLang, bannerData, setValue]);

  const onSubmit = async (data) => {
    const payload = {
      lang: activeLang,
      ...data,
    };
    const result = await dispatch(
      updateBannerFunc({ id: bannerId, data: payload })
    );
    if (updateBannerFunc.fulfilled.match(result)) {
      reset();
      navigate("/banners-settings");
    }
  };

  /**
   * Helpers
   */
  function buildFormDefaults(banner, translations) {
    return {
      title: translations.title,
      description: translations.description,
      btn_text: banner.btn_text,
      link: banner.link,
      is_active: banner.is_active,
      image: banner.image,
      mobile_image: banner.mobile_image,
    };
  }

  return (
    <>
      <PageTitle
        motherMenu="Home"
        activeMenu="Update Banner"
        customClasses="my-0"
      />

      <div className="relative flex items-center justify-end my-3">
        <Link to="/banners-settings">
          <button type="button" className="btn btn-secondary sw-btn-back me-2">
            {t("Back")}
          </button>
        </Link>
        <SubmitButton isLoading={submitLoading} form="submit-form" />
      </div>

      <div className="row">
        <div className="col-12">
          <LanguageSwitcherTabs
            languages={languageData?.languages || []}
            activeLang={activeLang}
            onChange={setActiveLang}
          />
        </div>

        <div className="col-12">
          <div className="card p-4">
            <form id="submit-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="form-group logo-input mb-3 col-sm-12 col-md-6">
                  <FormField
                    label="Title"
                    name="title"
                    register={register}
                    errors={errors}
                  />
                </div>
                <div className="form-group mb-3 col-sm-12 col-md-6">
                  <FormField
                    label="Button Text"
                    name="btn_text"
                    register={register}
                    errors={errors}
                  />
                </div>
                <div className="form-group mb-3 col-12">
                  <FormField
                    label="Path Link"
                    name="link"
                    register={register}
                    errors={errors}
                  />
                </div>
                <div className="form-group mb-3 col-12">
                  <CheckboxField
                    name="is_active"
                    label="Activity"
                    register={register}
                    errors={errors}
                  />
                </div>
              </div>

              <div className="row">
                <div className="form-group mb-3 col-12">
                  <TextAreaField
                    label="Description"
                    name="description"
                    isRichTextEditor
                    register={register}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                  />
                </div>
              </div>

              <div className="row">
                <div className="form-group mb-3 col-sm-12 col-md-6">
                  <MediaSelectorButton
                    label="Banner Image"
                    inputName="image"
                    required
                    register={register}
                    errors={errors}
                    onChange={(value) => setValue("image", value)}
                    defaultSelected={imageDefault}
                  />
                </div>
                <div className="form-group mb-3 col-sm-12 col-md-6">
                  <MediaSelectorButton
                    label="Banner Mobile Image"
                    inputName="mobile_image"
                    required
                    register={register}
                    errors={errors}
                    onChange={(value) => setValue("mobile_image", value)}
                    defaultSelected={imageMobDefault}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default UpdateBannerForm;
